import type { ConsentPreferencesT } from "@/app/types/consent";

// Strict id patterns: the ids are interpolated into inline scripts, so anything
// that is not a plain Google id must be rejected before it reaches the DOM.
const GTM_CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]+$/;
const GA_MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/;

export const GTM_SCRIPT_HOST = "https://www.googletagmanager.com";

type ConsentSignalT = "granted" | "denied";

export type ConsentModeStateT = {
  ad_storage: ConsentSignalT;
  ad_user_data: ConsentSignalT;
  ad_personalization: ConsentSignalT;
  analytics_storage: ConsentSignalT;
  functionality_storage: ConsentSignalT;
  personalization_storage: ConsentSignalT;
  security_storage: ConsentSignalT;
};

type DataLayerWindowT = Window & {
  dataLayer?: unknown[];
};

const toSignal = (granted: boolean): ConsentSignalT =>
  granted ? "granted" : "denied";

export function isValidGtmContainerId(value: string): boolean {
  return GTM_CONTAINER_ID_PATTERN.test(value);
}

export function isValidGaMeasurementId(value: string): boolean {
  return GA_MEASUREMENT_ID_PATTERN.test(value);
}

function assertGtmContainerId(containerId: string): void {
  if (!isValidGtmContainerId(containerId)) {
    throw new Error(`Invalid Google Tag Manager container id: ${containerId}`);
  }
}

function assertGaMeasurementId(measurementId: string): void {
  if (!isValidGaMeasurementId(measurementId)) {
    throw new Error(`Invalid GA4 measurement id: ${measurementId}`);
  }
}

// Maps the site's consent categories to Google Consent Mode v2 signals.
export function mapPreferencesToConsentMode(
  preferences: ConsentPreferencesT
): ConsentModeStateT {
  return {
    ad_storage: toSignal(preferences.marketing),
    ad_user_data: toSignal(preferences.marketing),
    ad_personalization: toSignal(preferences.marketing),
    analytics_storage: toSignal(preferences.analytics),
    functionality_storage: toSignal(preferences.functional),
    personalization_storage: toSignal(preferences.functional),
    security_storage: "granted",
  };
}

// Must run before gtm.js so tags can read the initial consent state.
export function buildConsentModeDefaultScript(
  preferences: ConsentPreferencesT
): string {
  const state = JSON.stringify(mapPreferencesToConsentMode(preferences));

  return `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('consent', 'default', ${state});
  `;
}

// Official GTM loader snippet. The noscript iframe is intentionally not used
// because it would load without consent.
export function buildGoogleTagManagerBootstrapScript(
  containerId: string
): string {
  assertGtmContainerId(containerId);

  return `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    '${GTM_SCRIPT_HOST}/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${containerId}');
  `;
}

export function buildGtagScriptSrc(measurementId: string): string {
  assertGaMeasurementId(measurementId);
  return `${GTM_SCRIPT_HOST}/gtag/js?id=${measurementId}`;
}

// Direct gtag.js config, used by tenants without a GTM container.
export function buildGtagBootstrapScript(measurementId: string): string {
  assertGaMeasurementId(measurementId);

  return `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${measurementId}');
  `;
}

// Pushes a Consent Mode update so already loaded tags react without a reload.
export function pushConsentUpdate(preferences: ConsentPreferencesT): void {
  if (typeof window === "undefined") return;

  const win = window as DataLayerWindowT;
  win.dataLayer = win.dataLayer || [];

  // gtag() requires the native arguments object, not an array.
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    win.dataLayer?.push(arguments);
  }

  (gtag as (...args: unknown[]) => void)(
    "consent",
    "update",
    mapPreferencesToConsentMode(preferences)
  );
}
